// M007-F010 / #60
//
// Admin Bookings groups recurring instances into a single row. Core's
// Google import writes google_recurring_event_id = NULL for a MASTER
// recurring row (it IS the master — google-calendar.service.ts's own
// convention, see the INSERT/UPDATE around lines 1758/1833). Such a row is
// a single entry in the bookings list, not a group of instances, but it
// still carries a real google_recurrence_rule (+ series_end_time) and must
// be recognised as a series here just as instance-based series already
// are — instead of silently falling into the "not recurring" branch, which
// is defect #60 (the identical bug already fixed for the Dashboard as #59,
// MR !313 — see src/components/Dashboard.js's own groupRecurringBookings).
//
// Kept as its own module (like adminRoomsOrganisationScope.js beside it) so
// this pure grouping logic can be unit tested directly, without pulling in
// AdminComponents.js's own top-level imports (services/api, AuthContext,
// websocket, etc).
import { parseRRuleToHumanReadable } from '../../utils/recurrenceParser';

export const groupRecurringBookings = (bookingsList) => {
  const grouped = [];
  const seenSeriesIds = new Set();

  for (const booking of bookingsList) {
    if (booking.google_recurring_event_id) {
      if (seenSeriesIds.has(booking.google_recurring_event_id)) {
        continue; // Skip - already have this series
      }
      seenSeriesIds.add(booking.google_recurring_event_id);

      // Count instances in this series
      const seriesInstances = bookingsList.filter(
        b => b.google_recurring_event_id === booking.google_recurring_event_id
      );

      const recurrenceLabel = booking.google_recurrence_rule
        ? parseRRuleToHumanReadable(booking.google_recurrence_rule)
        : 'Recurring series';

      grouped.push({
        ...booking,
        _isRecurringSeries: true,
        _recurrenceCount: seriesInstances.length,
        _recurrenceLabel: recurrenceLabel,
      });
    } else if (booking.google_recurrence_rule) {
      // #60: a MASTER row (no google_recurring_event_id, but it carries its
      // own rule) is a series of one row in this array. Reuse the same
      // parser instance-based series use above — never a second parser —
      // so the label is truthful: it reflects whatever the rule itself
      // says (an "until" date when the rule has one, nothing invented when
      // it doesn't).
      grouped.push({
        ...booking,
        _isRecurringSeries: true,
        _recurrenceCount: 1,
        _recurrenceLabel: parseRRuleToHumanReadable(booking.google_recurrence_rule),
      });
    } else {
      // Not a recurring instance - show as-is
      grouped.push(booking);
    }
  }

  return grouped;
};
